/**
 * Self-hosted licence banner (D-149): 30 days before the end of maintenance, the grace period,
 * then the limited mode (viewing and exports only, no data lost). Nothing in SaaS mode.
 */

import { type LicenceStatus, useLicenceStatusRetrieve } from "@agencyos/api-client";
import { OfflineBanner } from "@agencyos/ui-web";
import { useTranslation } from "react-i18next";

function formatDate(value: string | null | undefined, language: string): string {
  if (!value) return "";
  return new Intl.DateTimeFormat(language, { dateStyle: "long", timeZone: "UTC" }).format(new Date(`${value}T00:00:00Z`));
}

export function licenceMessageKey(status: LicenceStatus): string | null {
  switch (status.phase) {
    case "expiring":
      return (status.days_left ?? 0) <= 0 ? "licensing.endsToday" : "licensing.expiring";
    case "grace":
      return "licensing.grace";
    case "limited":
      return "licensing.limited";
    case "invalid":
      return "licensing.invalid";
    default:
      return null;
  }
}

export function LicenceBanner() {
  const { t, i18n } = useTranslation();
  const query = useLicenceStatusRetrieve({ query: { staleTime: 15 * 60_000, retry: false } });
  const status = query.data?.status === 200 ? query.data.data : undefined;
  if (!status || status.mode !== "self_hosted") return null;
  const key = licenceMessageKey(status);
  if (!key) return null;
  return (
    <OfflineBanner>
      {t(key, {
        count: status.days_left ?? 0,
        date: formatDate(status.maintenance_end, i18n.language),
        graceEnd: formatDate(status.grace_end, i18n.language),
      })}
    </OfflineBanner>
  );
}
